import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, Users, Shield, Building2, MapPin, ChevronLeft, 
  Upload, Eye, EyeOff, Copy, Check, CheckCircle, XCircle, 
  AlertTriangle, Download, Mail, FileSpreadsheet
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const AdminUserCreation = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  // Single user form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    language: 'en',
    state: '',
    district: ''
  });

  // Bulk upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);
  const [showPasswords, setShowPasswords] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [bulkRole, setBulkRole] = useState('');

  // Determine which roles the current user can create
  const getCreatableRoles = () => {
    const userRole = user?.role;
    if (userRole === 'super_admin' || user?.is_super_admin) {
      return [
        { value: 'state_admin', label: 'State Administrator', icon: Shield, color: 'bg-purple-500' },
        { value: 'district_admin', label: 'District Administrator', icon: Building2, color: 'bg-blue-500' },
        { value: 'frontline_worker', label: 'Frontline Worker', icon: Users, color: 'bg-green-500' }
      ];
    } else if (userRole === 'state_admin') {
      return [
        { value: 'district_admin', label: 'District Administrator', icon: Building2, color: 'bg-blue-500' },
        { value: 'frontline_worker', label: 'Frontline Worker', icon: Users, color: 'bg-green-500' }
      ];
    } else if (userRole === 'district_admin') {
      return [
        { value: 'frontline_worker', label: 'Frontline Worker', icon: Users, color: 'bg-green-500' }
      ];
    }
    return [];
  };

  const creatableRoles = getCreatableRoles();

  // Check if user can access this page
  const canAccessPage = ['super_admin', 'state_admin', 'district_admin'].includes(user?.role) || user?.is_super_admin;

  useEffect(() => {
    if (canAccessPage) {
      fetchStates();
    }
  }, [canAccessPage]);

  useEffect(() => {
    if (formData.state) {
      fetchDistricts(formData.state);
    } else {
      setDistricts([]);
    }
  }, [formData.state]);

  const fetchStates = async () => {
    try {
      const res = await api.getAvailableStates();
      let stateList = res.data.states || [];
      
      // Filter states based on user role
      if (user?.role === 'state_admin' && user?.assigned_states?.length > 0) {
        stateList = stateList.filter(s => user.assigned_states.includes(s.code) || user.primary_state === s.code);
      }
      
      setStates(stateList);
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
  };

  const fetchDistricts = async (stateCode) => {
    try {
      const res = await api.getDistrictsByState(stateCode);
      let districtList = res.data.districts || [];
      
      // Filter districts based on user role
      if (user?.role === 'district_admin' && user?.assigned_districts?.length > 0) {
        districtList = districtList.filter(d => user.assigned_districts.includes(d));
      }
      
      setDistricts(districtList);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
      setDistricts([]);
    }
  };

  const handleSingleUserSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setResult(null);

    try {
      const payload = {
        ...formData,
        created_by_admin: true
      };
      
      const res = await api.adminCreateUser(payload);
      setResult({
        success: true,
        user: res.data.user,
        tempPassword: res.data.temp_password,
        emailSent: res.data.email_sent
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        language: 'en',
        state: '',
        district: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Bulk upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.csv') && 
        !selectedFile.name.endsWith('.xlsx') && 
        !selectedFile.name.endsWith('.xls')) {
      alert('Please upload a CSV or Excel file');
      return;
    }
    setFile(selectedFile);
    setBulkResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.getUserUploadTemplateByRole(bulkRole);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bulkRole}_bulk_upload_template.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!file || !bulkRole) return;
    
    setUploading(true);
    setBulkResult(null);
    
    try {
      const response = await api.bulkUploadUsersByRole(file, bulkRole, sendEmails);
      setBulkResult(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      setBulkResult({
        total: 0,
        successful: 0,
        failed: 0,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Upload failed' }],
        created_users: []
      });
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'हिन्दी (Hindi)' },
    { value: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
    { value: 'ta', label: 'தமிழ் (Tamil)' },
    { value: 'te', label: 'తెలుగు (Telugu)' },
    { value: 'ml', label: 'മലയാളം (Malayalam)' },
    { value: 'mr', label: 'मराठी (Marathi)' },
    { value: 'ur', label: 'اردو (Urdu)' }
  ];

  if (!canAccessPage) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto text-gray-400 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-[#0F4C81] text-white rounded-lg"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-user-creation-page">
        <Navbar user={user} onLogout={onLogout} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-4 hover:underline"
              data-testid="back-button"
            >
              <ChevronLeft size={20} />
              <span>Back to Dashboard</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] p-3 rounded-2xl shadow-lg">
                <UserPlus className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#111827] font-['Nunito']" data-testid="page-title">
                  Create User Accounts
                </h1>
                <p className="text-base text-[#4B5563]">
                  {user?.role === 'super_admin' || user?.is_super_admin 
                    ? 'Create State Admins, District Admins, and Frontline Workers'
                    : user?.role === 'state_admin'
                    ? 'Create District Admins and Frontline Workers for your state'
                    : 'Create Frontline Workers for your district'}
                </p>
              </div>
            </div>
          </div>

          {/* Role Info Banner */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong>Your Role:</strong> {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              {user?.primary_state && <span className="ml-2">| <strong>State:</strong> {user.primary_state}</span>}
              {user?.assigned_districts?.length > 0 && <span className="ml-2">| <strong>Districts:</strong> {user.assigned_districts.join(', ')}</span>}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => setActiveTab('single')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'single'
                  ? 'bg-[#0F4C81] text-white shadow-lg'
                  : 'bg-white text-[#4B5563] border-2 border-slate-200 hover:border-[#0F4C81]'
              }`}
              data-testid="single-tab"
            >
              <UserPlus size={20} />
              <span>Single User</span>
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'bulk'
                  ? 'bg-[#0F4C81] text-white shadow-lg'
                  : 'bg-white text-[#4B5563] border-2 border-slate-200 hover:border-[#0F4C81]'
              }`}
              data-testid="bulk-tab"
            >
              <Upload size={20} />
              <span>Bulk Upload</span>
            </button>
          </div>

          {/* Single User Creation */}
          {activeTab === 'single' && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
              <h2 className="text-xl font-bold text-[#111827] mb-6">Create Single User</h2>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
                  {error}
                </div>
              )}

              {result?.success && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                    <div className="flex-1">
                      <p className="text-green-800 font-semibold">User created successfully!</p>
                      <p className="text-green-700 text-sm mt-1">
                        <strong>Email:</strong> {result.user.email}
                      </p>
                      <div className="mt-2 p-3 bg-white border-2 border-green-200 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Temporary Password:</p>
                        <div className="flex items-center justify-between">
                          <p className="font-mono font-bold text-lg text-[#0F4C81]">{result.tempPassword}</p>
                          <button
                            onClick={() => copyToClipboard(`${result.user.email} / ${result.tempPassword}`, 'single')}
                            className="p-2 hover:bg-green-100 rounded"
                          >
                            {copiedIndex === 'single' ? <Check className="text-green-600" size={18} /> : <Copy className="text-gray-500" size={18} />}
                          </button>
                        </div>
                      </div>
                      {result.emailSent && (
                        <p className="text-green-700 text-sm mt-2 flex items-center">
                          <Mail size={14} className="mr-1" /> Welcome email sent to user
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSingleUserSubmit} className="space-y-4">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Select Role *</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {creatableRoles.map((role) => {
                      const Icon = role.icon;
                      return (
                        <label
                          key={role.value}
                          className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.role === role.value
                              ? 'border-[#0F4C81] bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.value}
                            checked={formData.role === role.value}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="sr-only"
                          />
                          <div className={`w-10 h-10 ${role.color} rounded-full flex items-center justify-center mr-3`}>
                            <Icon className="text-white" size={20} />
                          </div>
                          <span className="font-semibold text-[#111827]">{role.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                      data-testid="name-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      data-testid="phone-input"
                    />
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Preferred Language</label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      data-testid="language-select"
                    >
                      {languageOptions.map((lang) => (
                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* State - Show for all roles except when district admin is creating */}
                  {(user?.role === 'super_admin' || user?.is_super_admin || user?.role === 'state_admin') && (
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">
                        State {formData.role !== 'frontline_worker' ? '*' : ''}
                      </label>
                      <select
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value, district: '' })}
                        className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                        required={formData.role === 'state_admin' || formData.role === 'district_admin'}
                        data-testid="state-select"
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.code} value={state.code}>{state.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* District - Show for district_admin and frontline_worker */}
                  {(formData.role === 'district_admin' || formData.role === 'frontline_worker') && (
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">
                        District {formData.role === 'district_admin' ? '*' : ''}
                      </label>
                      <input
                        type="text"
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        placeholder="Enter district name"
                        className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                        required={formData.role === 'district_admin'}
                        data-testid="district-input"
                      />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !formData.role || !formData.name || !formData.email}
                  className="w-full mt-6 flex items-center justify-center space-x-2 px-6 py-4 bg-[#0F4C81] text-white rounded-xl font-bold text-lg hover:bg-[#0d3d6b] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  data-testid="create-user-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={24} />
                      <span>Create User Account</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Bulk Upload */}
          {activeTab === 'bulk' && (
            <div className="space-y-6">
              {/* Role Selection for Bulk */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
                <h3 className="font-bold text-[#111827] mb-4">Step 1: Select Role for Bulk Upload</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {creatableRoles.map((role) => {
                    const Icon = role.icon;
                    return (
                      <label
                        key={role.value}
                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          bulkRole === role.value
                            ? 'border-[#0F4C81] bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`bulk-role-${role.value}`}
                        onClick={() => {
                          if (bulkRole !== role.value) {
                            setBulkRole(role.value);
                            setFile(null);
                            setBulkResult(null);
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="bulkRole"
                          value={role.value}
                          checked={bulkRole === role.value}
                          onChange={() => {}}
                          className="sr-only"
                        />
                        <div className={`w-10 h-10 ${role.color} rounded-full flex items-center justify-center mr-3`}>
                          <Icon className="text-white" size={20} />
                        </div>
                        <span className="font-semibold text-[#111827]">{role.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {bulkRole && (
                <>
                  {/* Instructions */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                    <h3 className="font-bold text-blue-800 mb-3 flex items-center">
                      <FileSpreadsheet className="mr-2" size={20} />
                      How Bulk Upload Works
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-blue-700 text-sm">
                      <li>Download the CSV template for {bulkRole.replace('_', ' ')}</li>
                      <li>Fill in user details (Name, Email, State, District as applicable)</li>
                      <li>Upload the completed file</li>
                      <li>System creates accounts with temporary passwords</li>
                      <li>Users receive welcome email with login credentials (optional)</li>
                    </ol>
                  </div>

                  {/* Download Template */}
                  <div className="bg-white rounded-2xl border-2 border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-[#111827] mb-1">Step 2: Download Template</h3>
                        <p className="text-sm text-[#6B7280]">
                          Get the CSV template with required columns for {bulkRole.replace('_', ' ')}
                        </p>
                      </div>
                      <button
                        onClick={handleDownloadTemplate}
                        className="flex items-center space-x-2 px-5 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
                        data-testid="download-template-button"
                      >
                        <Download size={20} />
                        <span>Download Template</span>
                      </button>
                    </div>
                  </div>

                  {/* Upload Section */}
                  <div className="bg-white rounded-2xl border-2 border-slate-200 p-6">
                    <h3 className="font-bold text-[#111827] mb-4">Step 3: Upload File</h3>
                    
                    {/* Drag & Drop Area */}
                    <div
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        dragActive 
                          ? 'border-[#10B981] bg-green-50' 
                          : file 
                            ? 'border-[#10B981] bg-green-50' 
                            : 'border-slate-300 hover:border-[#0F4C81]'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      data-testid="drop-zone"
                    >
                      {file ? (
                        <div className="flex flex-col items-center">
                          <CheckCircle className="text-[#10B981] mb-3" size={48} />
                          <p className="font-semibold text-[#111827]">{file.name}</p>
                          <p className="text-sm text-[#6B7280]">{(file.size / 1024).toFixed(1)} KB</p>
                          <button
                            onClick={() => setFile(null)}
                            className="mt-3 text-sm text-red-600 hover:underline"
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload className="text-[#9CA3AF] mb-3" size={48} />
                          <p className="font-semibold text-[#111827]">Drag & drop your file here</p>
                          <p className="text-sm text-[#6B7280] mb-4">or</p>
                          <label className="cursor-pointer">
                            <span className="px-5 py-2 bg-[#0F4C81] text-white rounded-lg font-semibold hover:bg-[#0d3d6b] transition-colors">
                              Browse Files
                            </span>
                            <input
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={(e) => handleFileSelect(e.target.files[0])}
                              className="hidden"
                              data-testid="file-input"
                            />
                          </label>
                          <p className="text-xs text-[#9CA3AF] mt-3">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                        </div>
                      )}
                    </div>

                    {/* Email Option */}
                    <div className="mt-4 flex items-center justify-between bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="text-[#0F4C81]" size={20} />
                        <div>
                          <p className="font-medium text-[#111827]">Send Welcome Emails</p>
                          <p className="text-xs text-[#6B7280]">Users will receive an email with their login credentials</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendEmails}
                          onChange={(e) => setSendEmails(e.target.checked)}
                          className="sr-only peer"
                          data-testid="send-emails-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#10B981]"></div>
                      </label>
                    </div>

                    {/* Upload Button */}
                    <button
                      onClick={handleBulkUpload}
                      disabled={!file || uploading}
                      className={`w-full mt-6 flex items-center justify-center space-x-2 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                        file && !uploading
                          ? 'bg-[#10B981] text-white hover:bg-[#059669] shadow-lg'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="upload-button"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={24} />
                          <span>Upload & Create Accounts</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Results */}
                  {bulkResult && (
                    <div className="bg-white rounded-2xl border-2 border-slate-200 p-6" data-testid="upload-results">
                      <h3 className="font-bold text-[#111827] mb-4">Upload Results</h3>
                      
                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-slate-50 rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-[#111827]">{bulkResult.total}</p>
                          <p className="text-sm text-[#6B7280]">Total Rows</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-[#10B981]">{bulkResult.successful}</p>
                          <p className="text-sm text-[#065F46]">Successful</p>
                        </div>
                        <div className={`rounded-xl p-4 text-center ${bulkResult.failed > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                          <p className={`text-3xl font-bold ${bulkResult.failed > 0 ? 'text-red-600' : 'text-[#111827]'}`}>
                            {bulkResult.failed}
                          </p>
                          <p className={`text-sm ${bulkResult.failed > 0 ? 'text-red-700' : 'text-[#6B7280]'}`}>Failed</p>
                        </div>
                      </div>

                      {/* Created Users */}
                      {bulkResult.created_users && bulkResult.created_users.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-[#111827] flex items-center">
                              <CheckCircle className="text-[#10B981] mr-2" size={18} />
                              Created Accounts ({bulkResult.created_users.length})
                            </h4>
                            <button
                              onClick={() => setShowPasswords(!showPasswords)}
                              className="flex items-center space-x-1 text-sm text-[#0F4C81] hover:underline"
                            >
                              {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                              <span>{showPasswords ? 'Hide' : 'Show'} Passwords</span>
                            </button>
                          </div>
                          
                          <div className="bg-green-50 rounded-xl border border-green-200 overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-green-100">
                                <tr>
                                  <th className="text-left py-2 px-4 font-semibold text-[#065F46]">Email</th>
                                  <th className="text-left py-2 px-4 font-semibold text-[#065F46]">Temporary Password</th>
                                  <th className="text-center py-2 px-4 font-semibold text-[#065F46]">Copy</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bulkResult.created_users.map((createdUser, index) => (
                                  <tr key={index} className="border-t border-green-200">
                                    <td className="py-2 px-4 text-[#111827]">{createdUser.email}</td>
                                    <td className="py-2 px-4 font-mono">
                                      {showPasswords ? createdUser.temp_password : '••••••••••'}
                                    </td>
                                    <td className="py-2 px-4 text-center">
                                      <button
                                        onClick={() => copyToClipboard(`${createdUser.email} / ${createdUser.temp_password}`, index)}
                                        className="p-1 hover:bg-green-200 rounded"
                                        title="Copy credentials"
                                      >
                                        {copiedIndex === index ? (
                                          <Check className="text-[#10B981]" size={16} />
                                        ) : (
                                          <Copy className="text-[#6B7280]" size={16} />
                                        )}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          
                          {!sendEmails && (
                            <p className="text-sm text-amber-600 mt-2 flex items-center">
                              <AlertTriangle size={14} className="mr-1" />
                              Welcome emails were not sent. Please share credentials manually.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Errors */}
                      {bulkResult.errors && bulkResult.errors.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                            <XCircle className="mr-2" size={18} />
                            Errors ({bulkResult.errors.length})
                          </h4>
                          <div className="bg-red-50 rounded-xl border border-red-200 max-h-60 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-red-100 sticky top-0">
                                <tr>
                                  <th className="text-left py-2 px-4 font-semibold text-red-800">Row</th>
                                  <th className="text-left py-2 px-4 font-semibold text-red-800">Email</th>
                                  <th className="text-left py-2 px-4 font-semibold text-red-800">Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bulkResult.errors.map((err, index) => (
                                  <tr key={index} className="border-t border-red-200">
                                    <td className="py-2 px-4 text-red-700">{err.row}</td>
                                    <td className="py-2 px-4 text-red-700">{err.email || '-'}</td>
                                    <td className="py-2 px-4 text-red-700">{err.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </>
  );
};

export default AdminUserCreation;
